import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { ReasonCode } from './reason-codes';

export type WorkflowDefinitionStatus = 'draft' | 'active' | 'archived';
export type WorkflowMode = 'active' | 'client_driven';

export interface WorkflowDefinition {
  id: string;
  orgId: string | null;
  productId: string | null;
  name: string;
  description: string | null;
  version: number;
  isTemplate: boolean;
  status: WorkflowDefinitionStatus;
  defaultMode: WorkflowMode;
  definition: unknown;
  workflowTimeoutSeconds: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
}

export interface WorkflowDefinitionList {
  items: WorkflowDefinition[];
}

export interface ListWorkflowsFilters {
  orgId?: string;
  productId?: string;
  status?: WorkflowDefinitionStatus;
}

const KEYS = {
  list: (filters: ListWorkflowsFilters) => ['workflows', 'list', filters] as const,
  detail: (id: string) => ['workflows', 'detail', id] as const,
};

function buildListUrl(filters: ListWorkflowsFilters): string {
  const params = new URLSearchParams();
  if (filters.orgId) params.set('org_id', filters.orgId);
  if (filters.productId) params.set('product_id', filters.productId);
  if (filters.status) params.set('status', filters.status);
  const qs = params.toString();
  return qs ? `/api/workflows?${qs}` : '/api/workflows';
}

export function useWorkflows(filters: ListWorkflowsFilters = {}) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () => apiGet<WorkflowDefinitionList>(buildListUrl(filters)),
  });
}

export function useWorkflow(id: string | undefined) {
  return useQuery({
    queryKey: id ? KEYS.detail(id) : ['workflows', 'detail', 'idle'],
    queryFn: () => apiGet<WorkflowDefinition>(`/api/workflows/${id}`),
    enabled: !!id,
  });
}

// Wire-format snake_case mirrors the admin-api controller's body shape
// (which mirrors the orchestrator's Zod CreateDefinitionDto). Kept
// snake_case here at the network boundary; UI components convert.
export interface CreateWorkflowInput {
  name: string;
  description?: string;
  /** Required for admin operators (no org on JWT). Ignored when `is_default: true`. */
  org_id?: string;
  product_id?: string;
  /**
   * Product-wide default — applies to every org with this product
   * until they author their own override. Only cross-tenant admins
   * may set this; tenant clients sending `true` are refused.
   */
  is_default?: boolean;
  default_mode: WorkflowMode;
  workflow_timeout_seconds: number;
  definition: unknown;
}

export function useCreateWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWorkflowInput) =>
      apiPost<WorkflowDefinition>('/api/workflows', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflows', 'list'] });
    },
  });
}

export interface UpdateWorkflowInput {
  name?: string;
  description?: string;
  definition?: unknown;
  default_mode?: WorkflowMode;
  workflow_timeout_seconds?: number;
  /** Reassign the workflow to a different (org, product). Draft only. */
  org_id?: string;
  product_id?: string;
  /**
   * Promote this draft to product-wide default (orgId = null). Refused
   * if another active default already exists for the product. Only
   * cross-tenant admins may set this.
   */
  is_default?: boolean;
}

export function useUpdateWorkflow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateWorkflowInput) =>
      apiPatch<WorkflowDefinition>(`/api/workflows/${id}`, input),
    onSuccess: (saved) => {
      // Refresh the cache so the editor's "loaded definition" baseline
      // shifts to the saved value (drives the dirty indicator).
      qc.setQueryData(['workflows', 'detail', id], saved);
      qc.invalidateQueries({ queryKey: ['workflows', 'list'] });
    },
  });
}

export interface ValidationResult {
  errors: string[];
  /** Non-blocking: surfaced to the operator but doesn't prevent publish. */
  warnings: string[];
}

/**
 * Run the orchestrator's validator against the *currently saved*
 * definition. The editor calls this after every successful Save Draft
 * so the operator can see what would block a publish before they try.
 * Mutation rather than Query because it's an action the operator
 * triggers via Save (not background-polled).
 */
export function useValidateWorkflow(id: string) {
  return useMutation({
    mutationFn: () => apiPost<ValidationResult>(`/api/workflows/${id}/validate`),
  });
}

/**
 * Publish flips status `draft → active`. Server runs validate first
 * and 4xxs with a JSON body of shape `{ message, errors }` if any
 * error remains — caller surfaces those into the editor's error panel.
 */
export function usePublishWorkflow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<WorkflowDefinition>(`/api/workflows/${id}/publish`),
    onSuccess: (saved) => {
      qc.setQueryData(['workflows', 'detail', id], saved);
      qc.invalidateQueries({ queryKey: ['workflows', 'list'] });
    },
  });
}

export interface CloneWorkflowInput {
  name: string;
  org_id?: string;
  product_id?: string;
}

/**
 * Cross-tenant clone. Creates a new draft definition under the target
 * `(org_id, product_id)` (defaults to the actor's org server-side).
 * Returns the new draft so the caller can navigate to its editor.
 */
export function useCloneWorkflow(sourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CloneWorkflowInput) =>
      apiPost<WorkflowDefinition>(`/api/workflows/${sourceId}/clone`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflows', 'list'] });
    },
  });
}

/**
 * Delete a workflow. The server hard-deletes when no workflow instances
 * reference it, and soft-deletes otherwise (FK chain preserved). The
 * `{ hardDeleted }` response lets the UI tailor its confirmation toast.
 */
export function useDeleteWorkflow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete<{ hardDeleted: boolean }>(`/api/workflows/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflows', 'list'] });
      qc.removeQueries({ queryKey: ['workflows', 'detail', id] });
    },
  });
}

/**
 * Mint a fresh draft version from a published (or archived) definition,
 * staying in the same `(orgId, productId, name)` and incrementing
 * `version`. Server refuses if the source is itself a draft.
 */
export function useNewVersionWorkflow(sourceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<WorkflowDefinition>(`/api/workflows/${sourceId}/new-version`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflows', 'list'] });
    },
  });
}

/**
 * Build the minimal valid `definition` JSON for a brand-new
 * client-driven workflow: a single `output` step whose return shape
 * is a placeholder string field. Passes the orchestrator's
 * terminal-reachability validator on first save so the operator can
 * mint the draft and start adding steps in the editor without first
 * having to know the schema.
 */
export function emptyClientDrivenDefinition(name: string): WorkflowDefinitionGraph {
  return {
    name,
    default_mode: 'client_driven',
    workflow_timeout_seconds: 86400,
    start_step: 'output_1',
    steps: {
      output_1: {
        kind: 'output',
        params: {
          fields: [{ name: 'result', type: 'string' }],
        },
      },
    },
  };
}

// ────────────────────────────────────────────────────────────────────────
// Definition-shape types — mirror the orchestrator's Zod schema for the
// new client-driven step kinds so the detail/preview UI can render the
// step graph + derive client payload schemas. Keep in sync with
// `services/orchestrator/src/definitions/definition-schema.ts`.
// ────────────────────────────────────────────────────────────────────────

import type { LocalizedString } from './localized-string';

/**
 * Translatable string on the wire. Existing question sets serialise
 * `prompt` and option `label` as plain strings; new authoring can
 * emit either a plain string (treated as the default / English) or
 * the full LocalizedString object. The editor normalises to the
 * canonical shape via `toLocalized()` internally and may emit either
 * shape on save via `denormalizeForWire()`.
 */
export type TranslatableString = string | LocalizedString;

export type AnswerSpec =
  | { type: 'free_text'; min_length?: number; max_length?: number }
  | {
      type: 'single_choice';
      options: Array<{ value: string; label: TranslatableString; icon?: string }>;
    }
  | {
      type: 'multi_choice';
      options: Array<{ value: string; label: TranslatableString; icon?: string }>;
      min_selected?: number;
      max_selected?: number;
    }
  | { type: 'boolean' }
  | { type: 'numeric'; min?: number; max?: number; integer?: boolean };

export interface QuestionSpec {
  id: string;
  prompt: TranslatableString;
  hint?: TranslatableString;
  required?: boolean;
  answer: AnswerSpec;
}

export type ImageConfiguration = 'derm_only' | 'derm_plus_macro' | 'macro_only';

export interface OutputFieldSpec {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'string_array' | 'object';
  source?: string;
}

export interface BaseStep {
  kind: string;
  params?: Record<string, unknown>;
  transitions?: Record<string, string>;
  timeout_seconds?: number;
  /**
   * Operator-authored display order — pure metadata for the admin UI.
   * The canvas renders a thin "next-by-order" arrow between
   * consecutive steps in this ordering so the operator can see their
   * intended reading sequence at a glance. Execution still follows
   * `transitions`. Contiguous 1..N maintained by the editor.
   */
  order?: number;
}

export interface CollectQuestionsStep extends BaseStep {
  kind: 'collect_questions';
  // Phase 5c — references a `QuestionSet` row by id instead of
  // embedding the questions inline. The orchestrator's runtime
  // snapshots the resolved questions into the parked step's input
  // JSON, so edits to the source set never affect already-running
  // instances.
  params: { question_set_id: string };
}

export interface CollectImagesStep extends BaseStep {
  kind: 'collect_images';
  params: {
    configurations: ImageConfiguration[];
    /**
     * Per-type count bounds. `optional: true` means the client may
     * submit zero of this type; if any are provided, the count must
     * fall in [min, max]. Cross-field `max >= min` enforced server-
     * side. Omitting the field leaves count unconstrained.
     */
    dermoscopic_count?: { min: number; max: number; optional?: boolean };
    macroscopic_count?: { min: number; max: number; optional?: boolean };
  };
}

export interface DecisionStep extends BaseStep {
  kind: 'decision';
  params: {
    input: { step_id: string; question_id: string };
    branches: Array<{ when: string | number | boolean | string[]; outcome: string }>;
    default_outcome?: string;
  };
}

export interface OutputStep extends BaseStep {
  kind: 'output';
  params: { fields: OutputFieldSpec[] };
}

export interface HaltStep extends BaseStep {
  kind: 'halt';
  params: { reason_code?: string };
}

export type { ReasonCode } from './reason-codes';

export function useHaltReasonCodes(orgId?: string | null) {
  return useQuery({
    queryKey: ['reason-codes', 'halt', orgId ?? 'system', false],
    queryFn: () => {
      const params = new URLSearchParams({ scope: 'halt' });
      if (orgId) params.set('org_id', orgId);
      return apiGet<ReasonCode[]>(`/api/workflows/reason-codes?${params.toString()}`);
    },
  });
}

export interface SetCaseStatusStep extends BaseStep {
  kind: 'set_case_status';
  params: { status: string };
}

export interface LoopControlStep extends BaseStep {
  kind: 'loop_control';
  // no params — repeat/done transitions carry all loop-control behavior
}

export interface WorkflowLoop {
  start: string;
  steps: string[];
  control: string;
  max_iterations?: number;
  per_finding?: boolean;
}

export type WorkflowStep =
  | CollectQuestionsStep
  | CollectImagesStep
  | DecisionStep
  | OutputStep
  | HaltStep
  | SetCaseStatusStep
  | LoopControlStep
  | BaseStep;

export interface WorkflowDefinitionGraph {
  name: string;
  description?: string;
  default_mode: WorkflowMode;
  workflow_timeout_seconds: number;
  start_step: string;
  steps: Record<string, WorkflowStep>;
  loops?: Record<string, WorkflowLoop>;
}
